import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchRsvpOpen } from '../../../utils/rsvpStatus';
import '../../../styles/LhyanneDebut.css';
import photo1 from '../../../assets/images/lhyanne/lhyanne-picture1.webp';
import photo2 from '../../../assets/images/lhyanne/lhyanne-picture2.webp';
import photo3 from '../../../assets/images/lhyanne/lhyanne-picture3.webp';
import photo4 from '../../../assets/images/lhyanne/lhyanne-picture4.webp';
import logicaLogo from '../../../assets/images/Logica (Black and White).webp';
import bgMusic from '../../../assets/sounds/lhyanne-bg-music.mpeg';

/* ── Constants ────────────────────────────────────────────────────── */
// May 5, 2026 · 6:00 PM Philippine Standard Time (UTC+8)
const EVENT_DATE = new Date('2026-05-05T18:00:00+08:00');
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/* ── Traditions Data ──────────────────────────────────────────────── */
const traditions = {
  roses: {
    label: '18 Roses',
    icon: '🌹',
    description: 'Eighteen remarkable gentlemen who will each offer a rose and a cherished wish to the debutante.',
    names: [
      'Special Person',
      'Johnlloyd Ubaldo',
      'Johnrick Ubaldo',
      'Regzime Garcia',
      'Kent De Leon',
      'Ej Mallari',
      'Rayn Carreon',
      'Matthew Sanchez',
      'Kiehl Mesa',
      'Naithan Sarmiento',
      'James Gaco',
      'Eathan David',
      'Bryan Tasic',
      'Ormando Llanto',
      'Jefferson Sevilla',
      'Rovic Adina',
      'Jerwin Tamayo',
      'Mike Miller',
    ],
  },
  candles: {
    label: '18 Candles',
    icon: '🕯️',
    description: 'Eighteen meaningful lights from those who illuminate the debutante\'s path forward.',
    names: [
      'Jell Yanne Ubaldo',
      'Johnnisa Ubaldo',
      'Melanie Canilao',
      'Azriel Cusipag',
      'Francine Telan',
      'Janelle Pasamonte',
      'Chloe Co',
      'Dorothy Villar',
      'Sara Dulay',
      'Audrey Macariola',
      'Aeyesha Bondoc',
      'Eulantha Feliciano',
      'Zoe Laxamana',
      'Rosemeinn Pineda',
      'Shamel Miranda',
      'Lhianne Agustin',
      'Kyla Allison',
      'Lindsay De Leon',
    ],
  },
  treasures: {
    label: '18 Treasures',
    icon: '💎',
    description: 'Eighteen precious gifts of wisdom and heartfelt love from those who treasure her most.',
    names: [
      'Beth Macabulos',
      'Cindy Gonzaga',
      'Pauline Silvestre',
      'Joane Escoro',
      'Jerry Caigan',
      'John Ubaldo',
      'Mary Anne',
      'Lourdes Nerosa',
      'Lorie Bundok',
      'Jemvie Malbacias',
      'Miole',
      'Jovelyn Damayo',
      'Sam Basa',
      'Jenelyn Bulahan',
      'Jerry Tamayo',
      'Gloria Pamintuan',
      'Jovy Ubaldo',
      // ⚠️ Note: One name may still be added. This list currently has 17 entries.
    ],
  },
  shots: {
    label: '18 Shots',
    icon: '🥂',
    description: 'A spirited celebration toast from eighteen beloved people raising a glass in her honor.',
    names: [
      'Jell Yanne Ubaldo',
      'John Lloyd Ubaldo',
      'Regzime Garcia',
      'Melanie Canilao',
      'Ej Mallari',
      'Naithan Sarmiento',
      'James Gaco',
      'Kyla Allison',
      'Lindsay De Leon',
      'Dorothy Villar',
      'Shin Junio',
      'Lhiane Garcia',
      'Chelsthea Lopez',
      'Zoe Laxamana',
      'Eulantha Feliciano',
      'Aeyesha Bondoc',
      'Jerico Nerosa',
      'Jeremy Nerosa',
    ],
  },
};

/* ── FAQ Data ─────────────────────────────────────────────────────── */
const faqs = [
  {
    q: 'When is the RSVP deadline?',
    a: 'Kindly confirm your attendance on or before April 30, 2026. Your early response helps us prepare a truly special evening for everyone. Please reach out to the family directly for any inquiries.',
  },
  {
    q: 'Is there parking available at the venue?',
    a: 'Yes, parking is available at Ossorio Property. We recommend arriving a little early to allow time to settle in and enjoy the enchanted atmosphere as the evening unfolds.',
  },
  {
    q: 'Will there be a seating arrangement?',
    a: 'Yes, a thoughtful seating arrangement will be in place to ensure every guest\'s comfort. Details will be shared with guests closer to the event date.',
  },
  {
    q: 'How do I send a gift?',
    a: 'Your presence on this magical evening is the most beautiful gift of all. If you wish to send a token of love, you are welcome to reach out to the family directly for guidance.',
  },
  {
    q: 'What time should guests arrive?',
    a: 'The celebration begins at 6:00 PM. We warmly suggest arriving 15 to 30 minutes early so you can settle in and enjoy the enchanted atmosphere as the evening unfolds.',
  },
  {
    q: 'What is the dress code?',
    a: 'Guests are invited to dress in Formal Attire. Ladies are encouraged to wear elegant gowns or formal dresses, and gentlemen are kindly requested to wear long-sleeved formal attire. The color preference is light blue, purple, green, yellow, and beige — guests are warmly welcome, though not required, to incorporate any of these into their look.',
  },
];

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════ */
export default function LhyanneDebut() {
  const [countdown, setCountdown]       = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [entered, setEntered]           = useState(false);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [activeTab, setActiveTab]       = useState('roses');
  const [openFaq, setOpenFaq]           = useState(null);
  const [galleryOpen, setGalleryOpen]   = useState(null);
  const [rsvpData, setRsvpData]         = useState({ name: '', email: '', phone: '', guests: '1', attendance: '', message: '' });
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);
  const [rsvpLoading, setRsvpLoading]   = useState(false);
  const [rsvpError, setRsvpError]       = useState('');
  const [fieldErrors, setFieldErrors]   = useState({});
  const [rsvpOpen, setRsvpOpen]         = useState(true);

  const audioRef = useRef(null);

  const galleryPhotos = [photo1, photo2, photo3, photo4];
  const galleryAlts   = [
    'Lhyanne Frankie in a lavender floral gown on the grass',
    'Lhyanne Frankie in a pink ruffled ballgown with mountain backdrop',
    'Lhyanne Frankie in a pink ballgown in a lush garden',
    'Lhyanne Frankie in a lavender gown by a bougainvillea garden',
  ];

  /* ── RSVP open/closed check ───────────────────────────────────── */
  useEffect(() => {
    fetchRsvpOpen('debut-lhyanne-frankie').then(setRsvpOpen);
  }, []);

  /* ── Countdown Timer ──────────────────────────────────────────── */
  useEffect(() => {
    const tick = () => {
      const diff = EVENT_DATE - new Date();
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setCountdown({
        days:    Math.floor(diff / 86_400_000),
        hours:   Math.floor((diff % 86_400_000) / 3_600_000),
        minutes: Math.floor((diff % 3_600_000) / 60_000),
        seconds: Math.floor((diff % 60_000) / 1_000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  /* ── Intersection Observer for Scroll Reveal ─────────────────── */
  useEffect(() => {
    const selectors = '.ld-reveal, .ld-reveal-left, .ld-reveal-right';
    const observer  = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('ld-visible');
          observer.unobserve(e.target);
        }
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll(selectors).forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [activeTab]); // re-run when tab changes so new list items are observed

  /* ── Keyboard close for lightbox ─────────────────────────────── */
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setGalleryOpen(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* ── Enter handler (splash screen) ─────────────────────────── */
  const handleEnter = useCallback(() => {
    setEntered(true);
    const audio = audioRef.current;
    if (!audio) return;
    audio.play()
      .then(() => setMusicPlaying(true))
      .catch(() => {});
  }, []);

  /* ── Music Toggle ─────────────────────────────────────────────── */
  const toggleMusic = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (musicPlaying) {
      audio.pause();
      setMusicPlaying(false);
    } else {
      audio.play()
        .then(() => setMusicPlaying(true))
        .catch(() => {
          // No source provided yet — gracefully do nothing
        });
    }
  }, [musicPlaying]);

  /* ── Smooth scroll helper ─────────────────────────────────────── */
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  /* ── RSVP Submit ──────────────────────────────────────────────── */
  const handleRsvpSubmit = async (e) => {
    e.preventDefault();

    // Inline field validation
    const errors = {};
    if (!rsvpData.name.trim() || rsvpData.name.trim().length < 2)
      errors.name = 'Please enter your full name (at least 2 characters).';
    if (rsvpData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rsvpData.email.trim()))
      errors.email = 'Please enter a valid email address.';
    if (rsvpData.phone.trim() && !/^[\d\s+\-().]{7,20}$/.test(rsvpData.phone.trim()))
      errors.phone = 'Please enter a valid phone number.';
    if (!rsvpData.attendance)
      errors.attendance = 'Please let us know if you will be attending.';
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }

    setFieldErrors({});
    setRsvpLoading(true);
    setRsvpError('');
    try {
      const res = await fetch(`${API_BASE_URL}/rsvp`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invitationId: 'debut-lhyanne-frankie',
          name:      rsvpData.name.trim(),
          email:     rsvpData.email.trim(),
          phone:     rsvpData.phone.trim(),
          guests:    rsvpData.guests,
          attending: rsvpData.attendance,
          message:   rsvpData.message.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Submission failed. Please try again.');
      setRsvpSubmitted(true);
    } catch (err) {
      setRsvpError(err.message || 'Something went wrong. Please try again or contact the family directly.');
    } finally {
      setRsvpLoading(false);
    }
  };

  /* ═════════════════════════════════════════════════════════════════
     RENDER
     ═════════════════════════════════════════════════════════════════ */
  return (
    <div className="ld-page">

      {/* ── Splash / Enter Screen ──────────────────────────────── */}
      {!entered && (
        <div className="ld-splash" onClick={handleEnter}>
          <div className="ld-splash-particles" aria-hidden="true">
            {Array.from({ length: 10 }).map((_, i) => <div key={i} className="ld-splash-particle" />)}
          </div>
          <div className="ld-splash-content">
            <span className="ld-splash-tag">You are invited to</span>
            <h1 className="ld-splash-name">Lhyanne Frankie</h1>
            <p className="ld-splash-subtitle">turns <em>eighteen</em></p>
            <p className="ld-splash-theme">✶ Ethereal Enchanted ✶</p>
            <button className="ld-splash-btn" onClick={handleEnter}>
              <span>✦</span> Step Into the Enchantment
            </button>
            <p className="ld-splash-note">Click anywhere to enter</p>
          </div>
        </div>
      )}

      {/* ── Floating Ambient Particles ──────────────────────────── */}
      <div className="ld-particles" aria-hidden="true">
        {Array.from({ length: 12 }).map((_, i) => <div key={i} className="ld-particle" />)}
      </div>

      {/* ── Music Toggle Button ─────────────────────────────────── */}
      <button
        className={`ld-music-btn${musicPlaying ? ' playing' : ''}`}
        onClick={toggleMusic}
        aria-label={musicPlaying ? 'Pause background music' : 'Play background music'}
        title={musicPlaying ? 'Pause Music' : 'Play Music'}
      >
        <span className="ld-music-icon">{musicPlaying ? '♫' : '♪'}</span>
      </button>

      {/* Hidden audio element */}
      <audio ref={audioRef} loop preload="auto">
        <source src={bgMusic} type="audio/mpeg" />
      </audio>

      {/* ══════════════════════════════════════════════════════════
          1. HERO — Full-screen cover
         ══════════════════════════════════════════════════════════ */}
      <section id="hero" className="ld-hero">
        <div className="ld-hero-bg">
          <img
            className="ld-hero-bg-img"
            src={photo1}
            alt="Lhyanne Frankie in a lavender floral gown"
          />
        </div>
        <div className="ld-hero-overlay" aria-hidden="true" />

        <div className="ld-hero-content">
          <span className="ld-hero-tag">You are warmly invited to celebrate</span>
          <h1 className="ld-hero-name">Lhyanne Frankie</h1>
          <p className="ld-hero-turns">Turns Eighteen</p>
          <p className="ld-hero-theme">✦ &nbsp; An Ethereal Enchanted Debut &nbsp; ✦</p>

          <div className="ld-hero-meta">
            <span className="ld-hero-date">Tuesday &nbsp;·&nbsp; May 5, 2026 &nbsp;·&nbsp; 6:00 PM</span>
            <span className="ld-hero-venue">Ossorio Property, Sinipit, Porac</span>
          </div>

          <button className="ld-hero-btn" onClick={() => scrollTo('welcome')}>
            Open Invitation &nbsp;✦
          </button>
        </div>

        <div className="ld-hero-scroll" aria-hidden="true">
          <div className="ld-hero-scroll-line" />
          <span>Scroll</span>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          2. COUNTDOWN
         ══════════════════════════════════════════════════════════ */}
      <section id="countdown" className="ld-countdown-section ld-section">
        <div className="ld-reveal">
          <span className="ld-section-tag">Counting down to the magic</span>
          <h2 className="ld-section-title">The Celebration Begins In</h2>
          <div className="ld-divider">
            <span className="ld-divider-line" /><span className="ld-divider-gem">✦</span><span className="ld-divider-line" />
          </div>
        </div>

        <div className="ld-countdown-grid">
          {[
            { num: countdown.days,    label: 'Days' },
            { num: countdown.hours,   label: 'Hours' },
            { num: countdown.minutes, label: 'Minutes' },
            { num: countdown.seconds, label: 'Seconds' },
          ].map(({ num, label }, i) => (
            <div key={label} className="ld-countdown-card ld-reveal" style={{ transitionDelay: `${i * 0.1}s` }}>
              <span className="ld-countdown-num">{String(num).padStart(2, '0')}</span>
              <span className="ld-countdown-label">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          3. WELCOME MESSAGE
         ══════════════════════════════════════════════════════════ */}
      <section id="welcome" className="ld-welcome-section ld-section">
        <div className="ld-welcome-inner">
          <div className="ld-reveal">
            <span className="ld-section-tag">A message from the heart</span>
            <h2 className="ld-section-title">Dear Beloved Guests,</h2>
            <div className="ld-divider">
              <span className="ld-divider-line" /><span className="ld-divider-gem">✦</span><span className="ld-divider-line" />
            </div>
          </div>

          <div className="ld-reveal" style={{ transitionDelay: '0.1s' }}>
            <p className="ld-welcome-text">
              With hearts overflowing with joy and gratitude, we invite you to witness
              a once-in-a-lifetime milestone — the blossoming of a remarkable young woman
              into all she is beautifully meant to be.
            </p>
          </div>

          <div className="ld-reveal" style={{ transitionDelay: '0.18s' }}>
            <p className="ld-welcome-text">
              <strong>Lhyanne Frankie</strong> is turning eighteen, and on this enchanted
              evening, we celebrate not merely a birthday, but the beginning of a radiant
              new chapter — a story being written in grace, in laughter, and in love that
              will last a lifetime.
            </p>
          </div>

          <div className="ld-reveal" style={{ transitionDelay: '0.26s' }}>
            <p className="ld-welcome-quote">
              "She blooms wherever she is planted,<br />
              and the whole world lights up around her."
            </p>
          </div>

          <div className="ld-reveal" style={{ transitionDelay: '0.34s' }}>
            <p className="ld-welcome-text">
              You have been chosen to be part of this magical night — not merely as a witness,
              but as a cherished soul whose warmth, prayers, and well-wishes mean everything
              to her and to our family. We cannot wait to share this dreamlike evening with you.
            </p>
          </div>

          <div className="ld-reveal" style={{ transitionDelay: '0.42s' }}>
            <p className="ld-welcome-text" style={{ textAlign: 'center', marginTop: '8px' }}>
              With all our love and excitement,
              <br />
              <span className="ld-welcome-signature">The Miller &amp; Damayo Family</span>
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          4. PHOTO GALLERY
         ══════════════════════════════════════════════════════════ */}
      <section id="gallery" className="ld-gallery-section ld-section">
        <div className="ld-reveal">
          <span className="ld-section-tag">A glimpse of the debutante</span>
          <h2 className="ld-section-title">Lhyanne Frankie</h2>
          <div className="ld-divider">
            <span className="ld-divider-line" /><span className="ld-divider-gem">✦</span><span className="ld-divider-line" />
          </div>
        </div>

        {/* Top: Featured (left) + Two stacked (right) */}
        <div className="ld-gallery-main-grid">
          <div
            className="ld-gallery-item ld-gallery-featured ld-reveal-left"
            onClick={() => setGalleryOpen(1)}
            role="button" tabIndex={0}
            aria-label="View photo 2"
            onKeyDown={(e) => e.key === 'Enter' && setGalleryOpen(1)}
          >
            <img src={photo2} alt={galleryAlts[1]} loading="lazy" />
            <div className="ld-gallery-overlay" aria-hidden="true">
              <span className="ld-gallery-overlay-icon">✦</span>
            </div>
          </div>

          <div className="ld-gallery-side">
            {[{ src: photo1, idx: 0 }, { src: photo3, idx: 2 }].map(({ src, idx }, i) => (
              <div
                key={idx}
                className="ld-gallery-item ld-reveal-right"
                style={{ transitionDelay: `${i * 0.14}s` }}
                onClick={() => setGalleryOpen(idx)}
                role="button" tabIndex={0}
                aria-label={`View photo ${idx + 1}`}
                onKeyDown={(e) => e.key === 'Enter' && setGalleryOpen(idx)}
              >
                <img src={src} alt={galleryAlts[idx]} loading="lazy" />
                <div className="ld-gallery-overlay" aria-hidden="true">
                  <span className="ld-gallery-overlay-icon">✦</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom: Full-width panoramic photo */}
        <div
          className="ld-gallery-bottom ld-reveal"
          onClick={() => setGalleryOpen(3)}
          role="button" tabIndex={0}
          aria-label="View photo 4"
          onKeyDown={(e) => e.key === 'Enter' && setGalleryOpen(3)}
        >
          <img src={photo4} alt={galleryAlts[3]} loading="lazy" />
          <div className="ld-gallery-overlay" aria-hidden="true">
            <span className="ld-gallery-overlay-icon">✦</span>
          </div>
        </div>
      </section>

      {/* Gallery Lightbox */}
      {galleryOpen !== null && (
        <div
          className="ld-lightbox"
          onClick={() => setGalleryOpen(null)}
          role="dialog" aria-modal="true" aria-label="Photo viewer"
        >
          <button
            className="ld-lightbox-close"
            onClick={() => setGalleryOpen(null)}
            aria-label="Close photo viewer"
          >
            ×
          </button>
          <img
            src={galleryPhotos[galleryOpen]}
            alt={galleryAlts[galleryOpen]}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          5. EVENT DETAILS
         ══════════════════════════════════════════════════════════ */}
      <section id="details" className="ld-details-section ld-section">
        <div className="ld-reveal">
          <span className="ld-section-tag">All the beautiful details</span>
          <h2 className="ld-section-title">Evening of Celebration</h2>
          <div className="ld-divider">
            <span className="ld-divider-line" /><span className="ld-divider-gem">✦</span><span className="ld-divider-line" />
          </div>
        </div>

        <div className="ld-details-card ld-reveal">
          <div className="ld-details-celebrant">
            <span className="ld-section-tag" style={{ marginBottom: '6px' }}>In honor of</span>
            <h3 className="ld-details-celebrant-name">Lhyanne Frankie</h3>
          </div>

          <div className="ld-details-grid">
            <div className="ld-detail-item">
              <span className="ld-detail-label">Date</span>
              <span className="ld-detail-value">Tuesday, May 5, 2026</span>
            </div>
            <div className="ld-detail-item">
              <span className="ld-detail-label">Time</span>
              <span className="ld-detail-value">6:00 PM</span>
            </div>
            <div className="ld-detail-item ld-detail-full">
              <span className="ld-detail-label">Venue</span>
              <span className="ld-detail-value">Ossorio Property, Sinipit, Porac</span>
            </div>
            <div className="ld-detail-item">
              <span className="ld-detail-label">Outfit Changes</span>
              <span className="ld-detail-value">3 Stunning Looks</span>
            </div>
            <div className="ld-detail-item">
              <span className="ld-detail-label">Color Preference</span>
              <span className="ld-detail-value">
                <span className="ld-motif-dots">
                  <span className="ld-motif-dot" style={{ background: '#A8C8E8' }} aria-label="Light blue" />
                  <span className="ld-motif-dot" style={{ background: '#9B7DC8' }} aria-label="Purple" />
                  <span className="ld-motif-dot" style={{ background: '#7DB87D' }} aria-label="Green" />
                  <span className="ld-motif-dot" style={{ background: '#E8D870' }} aria-label="Yellow" />
                  <span className="ld-motif-dot" style={{ background: '#D4C4A0' }} aria-label="Beige" />
                </span>
                <span style={{ fontSize: '0.92rem', fontStyle: 'italic', color: 'var(--ld-text-mid)' }}>Light blue, purple, green, yellow &amp; beige</span>
              </span>
            </div>
          </div>

          <div className="ld-details-family">
            <div className="ld-family-row">
              <div className="ld-family-person">
                <span className="ld-family-role">Father</span>
                <span className="ld-family-name">Mike Miller</span>
              </div>
              <div className="ld-family-person">
                <span className="ld-family-role">Mother</span>
                <span className="ld-family-name">Lucel Damayo</span>
              </div>
            </div>
            <div className="ld-details-siblings">
              <span className="ld-siblings-label">Siblings</span>
              <span className="ld-siblings-names">Jell Yanne Ubaldo &amp; Lucas Miller</span>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          6. DRESS CODE
         ══════════════════════════════════════════════════════════ */}
      <section id="dresscode" className="ld-dresscode-section ld-section">
        <div className="ld-reveal">
          <span className="ld-section-tag">An enchanted evening awaits you</span>
          <h2 className="ld-section-title">Dress Code</h2>
          <div className="ld-divider">
            <span className="ld-divider-line" /><span className="ld-divider-gem">✦</span><span className="ld-divider-line" />
          </div>
        </div>

        <div className="ld-dresscode-card ld-reveal">
          <span className="ld-dresscode-icon" aria-hidden="true">👗</span>
          <h3 className="ld-dresscode-title">Formal Attire</h3>
          <p className="ld-dresscode-text">
            In the spirit of this enchanted evening, guests are warmly invited to dress in
            their finest formal wear. Ladies are encouraged to grace the celebration in
            elegant gowns or sophisticated formal dresses befitting a magical debut ball.
          </p>
          <p className="ld-dresscode-text">
            Gentlemen are kindly requested to wear long-sleeved formal attire as a mark of
            respect and celebration for the debutante. To honor Lhyanne's chosen color preference,
            guests are warmly welcome — though not required — to incorporate touches of{' '}
            <strong>light blue, purple, green, yellow, or beige</strong> into their ensembles.
          </p>
          <div className="ld-dresscode-pills">
            <span className="ld-dresscode-pill ladies">👸 Ladies: Formal Gown or Dress</span>
            <span className="ld-dresscode-pill gents">🤵 Gentlemen: Long-sleeved Formal</span>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          7. TRADITIONS / PROGRAM HIGHLIGHTS
         ══════════════════════════════════════════════════════════ */}
      <section id="traditions" className="ld-traditions-section ld-section">
        <div className="ld-reveal">
          <span className="ld-section-tag">An enchanted evening of tradition</span>
          <h2 className="ld-section-title">Program Highlights</h2>
          <p className="ld-section-subtitle">
            The timeless rituals of a debut celebration — each one a meaningful tribute
            to the young woman she has become.
          </p>
          <div className="ld-divider">
            <span className="ld-divider-line" /><span className="ld-divider-gem">✦</span><span className="ld-divider-line" />
          </div>
        </div>

        <div className="ld-traditions-tabs ld-reveal">
          {Object.entries(traditions).map(([key, { label, icon }]) => (
            <button
              key={key}
              className={`ld-tab-btn${activeTab === key ? ' active' : ''}`}
              onClick={() => setActiveTab(key)}
              aria-pressed={activeTab === key}
            >
              {icon} {label}
            </button>
          ))}
        </div>

        <div className="ld-traditions-content">
          {(() => {
            const t = traditions[activeTab];
            return (
              <>
                <div className="ld-tradition-header ld-reveal">
                  <span className="ld-tradition-icon" aria-hidden="true">{t.icon}</span>
                  <h3 className="ld-tradition-title">{t.label}</h3>
                  <p className="ld-tradition-description">{t.description}</p>
                </div>
                <ul className="ld-tradition-list" aria-label={`${t.label} participants`}>
                  {t.names.map((name, i) => (
                    <li key={i} className="ld-tradition-item ld-reveal" style={{ transitionDelay: `${i * 0.04}s` }}>
                      <span className="ld-tradition-num" aria-hidden="true">{i + 1}</span>
                      <span className="ld-tradition-name">{name}</span>
                    </li>
                  ))}
                </ul>
              </>
            );
          })()}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          8. VENUE
         ══════════════════════════════════════════════════════════ */}
      <section id="venue" className="ld-venue-section ld-section">
        <div className="ld-reveal">
          <span className="ld-section-tag">Find your way to the magic</span>
          <h2 className="ld-section-title">The Venue</h2>
          <div className="ld-divider">
            <span className="ld-divider-line" /><span className="ld-divider-gem">✦</span><span className="ld-divider-line" />
          </div>
        </div>

        <div className="ld-venue-card ld-reveal">
          <div className="ld-venue-info">
            <span className="ld-venue-icon" aria-hidden="true">📍</span>
            <h3 className="ld-venue-name">Ossorio Property</h3>
            <p className="ld-venue-address">Sinipit, Porac, Pampanga</p>
            <a
              className="ld-venue-btn"
              href="https://maps.app.goo.gl/wYcKjeDJQN1fnGMR8"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View venue on Google Maps"
            >
              🗺 &nbsp; View on Google Maps
            </a>
            <p className="ld-venue-note">✦ &nbsp; Ossorio Property, Sinipit, Porac</p>
          </div>
          <div className="ld-map-embed" aria-label="Map of Ossorio Property, Sinipit, Porac, Pampanga">
            <iframe
              title="Ossorio Property, Sinipit, Porac, Pampanga"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1927.3!2d120.5740126!3d15.1114331!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3396f3400d2450cb%3A0xb6cb1b206af67502!2sOssorio%20property!5e0!3m2!1sen!2sph!4v1713427200000!5m2!1sen!2sph"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          10. FAQ ACCORDION
         ══════════════════════════════════════════════════════════ */}
      <section id="faq" className="ld-faq-section ld-section">
        <div className="ld-reveal">
          <span className="ld-section-tag">Everything you need to know</span>
          <h2 className="ld-section-title">Frequently Asked Questions</h2>
          <div className="ld-divider">
            <span className="ld-divider-line" /><span className="ld-divider-gem">✦</span><span className="ld-divider-line" />
          </div>
        </div>

        <div className="ld-faq-list" role="list">
          {faqs.map((faq, i) => {
            const isOpen = openFaq === i;
            return (
              <div key={i} className="ld-faq-item ld-reveal" style={{ transitionDelay: `${i * 0.07}s` }} role="listitem">
                <button
                  className="ld-faq-question"
                  onClick={() => setOpenFaq(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${i}`}
                >
                  <span className="ld-faq-q-text">{faq.q}</span>
                  <span className={`ld-faq-chevron${isOpen ? ' open' : ''}`} aria-hidden="true">▾</span>
                </button>
                <div
                  id={`faq-answer-${i}`}
                  className={`ld-faq-answer${isOpen ? ' open' : ''}`}
                  role="region"
                  aria-hidden={!isOpen}
                >
                  <div className="ld-faq-answer-inner">{faq.a}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          11. RSVP FORM
         ══════════════════════════════════════════════════════════ */}
      <section id="rsvp" className="ld-rsvp-section ld-section">
        <div className="ld-reveal">
          <span className="ld-section-tag">Reserve your place</span>
          <h2 className="ld-section-title">RSVP</h2>
          <p className="ld-section-subtitle">
            We would love to know you will be joining us. Kindly let us know
            at your earliest convenience.
          </p>
          <div className="ld-divider">
            <span className="ld-divider-line" /><span className="ld-divider-gem">✦</span><span className="ld-divider-line" />
          </div>
        </div>

        <div className="ld-rsvp-card ld-reveal">
          {!rsvpOpen ? (
            <div className="ld-rsvp-success" role="alert">
              <span className="ld-rsvp-success-icon" aria-hidden="true">🔒</span>
              <h3>RSVPs are now closed</h3>
              <p>Thank you for your interest. The RSVP period for this event has ended.</p>
            </div>
          ) : rsvpSubmitted ? (
            <div className="ld-rsvp-success" role="alert">
              <span className="ld-rsvp-success-icon" aria-hidden="true">✨</span>
              <h3>Thank you so much!</h3>
              <p>
                {rsvpData.attendance === 'yes'
                  ? "We are so excited to celebrate this magical night with you. See you on May 5th!"
                  : "We completely understand, and we will surely miss you. Thank you for letting us know — you will be in our hearts that evening."}
              </p>
            </div>
          ) : (
            <form className="ld-form" onSubmit={handleRsvpSubmit} noValidate>
              <div className="ld-form-row">
                <div className="ld-form-group">
                  <label className="ld-form-label" htmlFor="rsvp-name">Full Name *</label>
                  <input
                    id="rsvp-name"
                    className={`ld-form-input${fieldErrors.name ? ' ld-input-error' : ''}`}
                    type="text"
                    placeholder="Your full name"
                    value={rsvpData.name}
                    onChange={(e) => { setRsvpData({ ...rsvpData, name: e.target.value }); setFieldErrors((p) => ({ ...p, name: '' })); }}
                    autoComplete="name"
                  />
                  {fieldErrors.name && <span className="ld-field-error">{fieldErrors.name}</span>}
                </div>
                <div className="ld-form-group">
                  <label className="ld-form-label" htmlFor="rsvp-email">Email Address</label>
                  <input
                    id="rsvp-email"
                    className={`ld-form-input${fieldErrors.email ? ' ld-input-error' : ''}`}
                    type="email"
                    placeholder="your@email.com"
                    value={rsvpData.email}
                    onChange={(e) => { setRsvpData({ ...rsvpData, email: e.target.value }); setFieldErrors((p) => ({ ...p, email: '' })); }}
                    autoComplete="email"
                  />
                  {fieldErrors.email && <span className="ld-field-error">{fieldErrors.email}</span>}
                </div>
              </div>

              <div className="ld-form-row">
                <div className="ld-form-group">
                  <label className="ld-form-label" htmlFor="rsvp-phone">Phone Number</label>
                  <input
                    id="rsvp-phone"
                    className={`ld-form-input${fieldErrors.phone ? ' ld-input-error' : ''}`}
                    type="tel"
                    placeholder="+63 XXX XXX XXXX"
                    value={rsvpData.phone}
                    onChange={(e) => { setRsvpData({ ...rsvpData, phone: e.target.value }); setFieldErrors((p) => ({ ...p, phone: '' })); }}
                    autoComplete="tel"
                  />
                  {fieldErrors.phone && <span className="ld-field-error">{fieldErrors.phone}</span>}
                </div>
                <div className="ld-form-group">
                  <label className="ld-form-label" htmlFor="rsvp-guests">Number of Guests *</label>
                  <select
                    id="rsvp-guests"
                    className="ld-form-input ld-form-select"
                    value={rsvpData.guests}
                    onChange={(e) => setRsvpData({ ...rsvpData, guests: e.target.value })}
                  >
                    {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                      <option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ld-form-group">
                <span className="ld-form-label" id="attendance-label">Will you be attending? *</span>
                <div className="ld-attendance-group" role="group" aria-labelledby="attendance-label">
                  <button
                    type="button"
                    className={`ld-attendance-btn${rsvpData.attendance === 'yes' ? ' selected-yes' : ''}`}
                    onClick={() => { setRsvpData({ ...rsvpData, attendance: 'yes' }); setFieldErrors((p) => ({ ...p, attendance: '' })); }}
                    aria-pressed={rsvpData.attendance === 'yes'}
                  >
                    ✓ &nbsp; Yes, I'll be there!
                  </button>
                  <button
                    type="button"
                    className={`ld-attendance-btn${rsvpData.attendance === 'no' ? ' selected-no' : ''}`}
                    onClick={() => { setRsvpData({ ...rsvpData, attendance: 'no' }); setFieldErrors((p) => ({ ...p, attendance: '' })); }}
                    aria-pressed={rsvpData.attendance === 'no'}
                  >
                    ✗ &nbsp; Sorry, I can't make it
                  </button>
                </div>
                {fieldErrors.attendance && <span className="ld-field-error">{fieldErrors.attendance}</span>}
              </div>

              <div className="ld-form-group">
                <label className="ld-form-label" htmlFor="rsvp-message">Leave a Message</label>
                <textarea
                  id="rsvp-message"
                  className="ld-form-textarea"
                  placeholder="Share your wishes or a heartfelt message for Lhyanne..."
                  value={rsvpData.message}
                  onChange={(e) => setRsvpData({ ...rsvpData, message: e.target.value })}
                />
              </div>

              {rsvpError && (
                <div className="ld-error-msg" role="alert">{rsvpError}</div>
              )}

              <button
                type="submit"
                className="ld-submit-btn"
                disabled={rsvpLoading}
              >
                {rsvpLoading ? 'Sending…' : 'Send My RSVP ✦'}
              </button>

              <p className="ld-rsvp-note">
                ✦ &nbsp; Kindly RSVP on or before <strong>April 30, 2026</strong>.<br />
                You may also reach out directly to the family for any inquiries.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          12. FOOTER
         ══════════════════════════════════════════════════════════ */}
      <footer id="footer" className="ld-footer">
        <div className="ld-reveal">
          <p className="ld-footer-script">Lhyanne Frankie</p>
          <p className="ld-footer-turns">Turns Eighteen &nbsp;·&nbsp; May 5, 2026</p>
          <p className="ld-footer-message">
            Thank you for being one of the cherished souls invited to witness
            this magical evening. Your love, your laughter, and your presence
            mean everything to us. We cannot wait to celebrate with you.
          </p>
          <hr className="ld-footer-divider" aria-hidden="true" />
          <p className="ld-footer-credit">✦ &nbsp; An Ethereal Enchanted Debut</p>
          <div className="ld-watermark">
            <p>Created with ❤️ by</p>
            <a href="https://logicatechnology.com" target="_blank" rel="noopener noreferrer" aria-label="Visit Logica Technology">
              <img src={logicaLogo} alt="Logica Technology" />
            </a>
          </div>
        </div>
      </footer>

    </div>
  );
}
